import { useState } from "react";
import { toast } from "react-toastify";
import {
  generatePresignedUrlsEntrega,
  PresignedUrlRequestItem,
  PresignedUrlResponseItem,
  salvarFotosEntrega,
  uploadToPresignedUrl,
} from "../services/fotosEntregaService";

const mimeTypeToExtension: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

const extractMimeType = (dataUrl: string): string => {
  const match = dataUrl.match(/^data:(.*?);base64,/);
  return match?.[1] || "image/jpeg";
};

const dataUrlToBlob = (dataUrl: string): Blob => {
  const [metadata, content] = dataUrl.split(",");
  const mimeType = metadata.match(/data:(.*?);base64/)?.[1] || "image/jpeg";
  const byteString = atob(content || "");
  const byteArray = new Uint8Array(byteString.length);

  for (let index = 0; index < byteString.length; index += 1) {
    byteArray[index] = byteString.charCodeAt(index);
  }

  return new Blob([byteArray], { type: mimeType });
};

const resolveUploadUrl = (item: PresignedUrlResponseItem): string => {
  const url =
    item.uploadUrl || item.url || item.putUrl || item.presignedUrl || "";

  return String(url)
    .replace(/%22/g, "")
    .replace(/^"+|"+$/g, "");
};

const resolveFileKey = (item: PresignedUrlResponseItem): string => {
  return String(item.fileKey || item.file_key || item.key || "");
};

export const useEnviarFotosEntrega = () => {
  const [loading, setLoading] = useState(false);

  const enviarFotosEntrega = async (
    token: string,
    veiculoId: number,
    imagensBase64: string[]
  ): Promise<boolean> => {
    if (!imagensBase64.length) {
      toast.warning("Tire ao menos uma foto antes de confirmar.");
      return false;
    }

    try {
      setLoading(true);

      const presignedPayload: PresignedUrlRequestItem[] = imagensBase64.map(
        (dataUrl, index) => {
          const mimeType = extractMimeType(dataUrl);
          const extension = mimeTypeToExtension[mimeType] || "jpg";

          return {
            mimeType,
            nomeOriginal: `VeiculoFotosAnexadas/${veiculoId}/${Date.now()}_${index + 1}.${extension}`,
          };
        }
      );

      const presignedUrls = await generatePresignedUrlsEntrega(
        token,
        presignedPayload
      );

      if (presignedUrls.length !== imagensBase64.length) {
        throw new Error("Quantidade de URLs assinadas diferente das imagens");
      }

      await Promise.all(
        presignedUrls.map((presigned, index) => {
          const uploadUrl = resolveUploadUrl(presigned);
          if (!uploadUrl) {
            throw new Error("Presigned URL ausente para upload");
          }

          const mimeType = extractMimeType(imagensBase64[index]);
          const blob = dataUrlToBlob(imagensBase64[index]);

          return uploadToPresignedUrl(uploadUrl, blob, mimeType);
        })
      );

      const fileKeys = presignedUrls
        .map((item) => resolveFileKey(item))
        .filter(Boolean);

      if (fileKeys.length !== imagensBase64.length) {
        throw new Error("Numero de fileKey diferente do numero de fotos");
      }

      await salvarFotosEntrega(
        token,
        veiculoId,
        fileKeys.map((fileKey, index) => ({
          caminho_foto: fileKey,
          ferramenta: index === 0 ? "Entrega" : undefined,
        }))
      );

      toast.success("Fotos enviadas com sucesso.");
      return true;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao enviar fotos da entrega.";
      toast.error(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    enviarFotosEntrega,
    loading,
  };
};
